import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { Button } from "components/ui/button"
import { Badge } from "components/ui/badge"
import { useDialogModal } from "@/hook/handle-modal/hooks/actions"
import { formatToBRL } from "@/utils/formatToMoney"
import { formatOperatorName } from "@/utils/format-operator-name"
import { getEmailTypeLabel } from "@/pages/conciliation/email-report/components/conciliation-email-report-types"
import type { EmailData, Divergence, Nsu } from "../../types"

interface EmailDetailsModalProps {
  email: EmailData
}

const getStatusVariant = (status: string) => {
  switch (status) {
    case 'APPROVED':
      return 'default'
    case 'REJECTED':
      return 'destructive'
    case 'PENDING':
    default:
      return 'secondary'
  }
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'APPROVED':
      return 'Aprovado'
    case 'REJECTED':
      return 'Reprovado'
    case 'PENDING':
    default:
      return 'Pendente'
  }
}

export const EmailDetailsModal = ({ email }: EmailDetailsModalProps) => {
  const { handleModal } = useDialogModal()

  return {
    element: (
      <div className="space-y-6 max-w-4xl">
        <div className="border-b pb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold">
              E-mail #{email.email_id} - {getEmailTypeLabel(email.type)}
            </h3>
            <Badge variant={getStatusVariant(email.status)} className="text-xs">
              {getStatusLabel(email.status)}
            </Badge>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
            <div>
              <span className="font-medium">Operador:</span>
              <br />
              {formatOperatorName(email.requested_by_email)}
            </div>
            <div>
              <span className="font-medium">Enviado em:</span>
              <br />
              {format(new Date(email.sent_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
            </div>
            <div>
              <span className="font-medium">Total de Itens:</span>
              <br />
              {email.totalItems}
            </div>
            <div>
              <span className="font-medium">Finalizado:</span>
              <br />
              <Badge variant={email.is_finalized ? "default" : "secondary"}>
                {email.is_finalized ? "Sim" : "Não"}
              </Badge>
            </div>
          </div>
        </div>

        {email.divergences.length > 0 && (
          <div>
            <h4 className="text-md font-semibold mb-3 text-red-700">
              Divergências ({email.divergences.length})
            </h4>

            <div className="space-y-3 max-h-60 overflow-y-auto">
              {email.divergences.map((divergence: Divergence) => (
                <div key={divergence.id} className="p-4 border rounded-lg bg-red-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-sm">{divergence.name}</div>
                    <div className="flex items-center gap-2">
                      <Badge variant={getStatusVariant(divergence.review_status)} className="text-xs">
                        {getStatusLabel(divergence.review_status)}
                      </Badge>
                      <span className="font-semibold text-red-600">
                        {formatToBRL(divergence.amount)}
                      </span>
                    </div>
                  </div>

                  <div className="text-xs text-gray-600 mb-2">
                    Operador: {divergence.operator} | Store: {divergence.store_code}
                  </div>

                  <div className="text-xs text-gray-600 mb-2">
                    <span className="font-medium">Justificativa da loja:</span>
                    <br />
                    {divergence.store_justification}
                  </div>

                  {divergence.review_notes && (
                    <div className="bg-white p-2 rounded border-l-4 border-red-200">
                      <p className="text-xs text-gray-700">
                        <strong>Observação:</strong> {divergence.review_notes}
                      </p>
                      {divergence.reviewed_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          Revisado em: {format(new Date(divergence.reviewed_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                          {divergence.reviewed_by && ` por ${divergence.reviewed_by.split("@")[0]}`}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {email.nsus.length > 0 && (
          <div>
            <h4 className="text-md font-semibold mb-3 text-blue-700">
              NSUs ({email.nsus.length})
            </h4>

            <div className="space-y-3 max-h-60 overflow-y-auto">
              {email.nsus.map((nsu: Nsu) => (
                <div key={nsu.id} className="p-4 border rounded-lg bg-blue-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-sm">{nsu.number}</div>
                    <div className="flex items-center gap-2">
                      <Badge variant={getStatusVariant(nsu.review_status)} className="text-xs">
                        {getStatusLabel(nsu.review_status)}
                      </Badge>
                      <span className="font-semibold text-blue-600">
                        {formatToBRL(nsu.amount)}
                      </span>
                    </div>
                  </div>

                  <div className="text-xs text-gray-600 mb-2">
                    Operadora: {nsu.operator}
                  </div>

                  {nsu.review_notes && (
                    <div className="bg-white p-2 rounded border-l-4 border-blue-200">
                      <p className="text-xs text-gray-700">
                        <strong>Observação:</strong> {nsu.review_notes}
                      </p>
                      {nsu.reviewed_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          Revisado em: {format(new Date(nsu.reviewed_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                          {nsu.reviewed_by && ` por ${nsu.reviewed_by.split("@")[0]}`}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={() => handleModal({ isOpen: false })}>
            Fechar
          </Button>
        </div>
      </div>
    ),
    title: `Detalhes do E-mail #${email.email_id}`
  }
}
